import { useEffect, useMemo, useState, type FormEvent } from "react";
import { hotTopics, featuredStories } from "@/mocks/suye";

interface NavLink {
  href: string;
  label: string;
}

const navLinks: NavLink[] = [
  { href: "#live", label: "现场围观" },
  { href: "#hot", label: "实时热榜" },
  { href: "#cats", label: "话题分类" },
  { href: "#feature", label: "热门专题" },
  { href: "#square", label: "围观广场" },
  { href: "#about", label: "关于宿野" },
];

interface SearchItem {
  title: string;
  target: string;
  tag: string;
}

const buildSearchPool = (): SearchItem[] => [
  ...hotTopics.map((h) => ({ title: h.title, target: "#hot", tag: "热榜" })),
  ...featuredStories.map((f) => ({ title: f.title, target: "#feature", tag: "专题" })),
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const light = !scrolled;

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className={
          "transition-colors duration-300 " +
          (scrolled
            ? "border-b border-background-200/70 bg-background-50/90 backdrop-blur-md"
            : "border-b border-background-50/10 bg-transparent")
        }
      >
        <div className="mx-auto flex h-[72px] max-w-[1280px] items-center justify-between gap-4 px-4 md:px-6">
          <a href="#top" className="flex shrink-0 items-center gap-2.5">
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-xl font-heading text-xl font-black transition-colors " +
                (light ? "bg-background-50/95 text-primary-600" : "bg-primary-500 text-background-50")
              }
            >
              瓜
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={
                  "font-heading text-xl font-black tracking-tight transition-colors " +
                  (light ? "text-background-50" : "text-foreground-950")
                }
              >
                宿野<span className="text-primary-500">吃瓜</span>
              </span>
              <span
                className={
                  "mt-1 text-[10px] tracking-[0.22em] uppercase transition-colors " +
                  (light ? "text-background-200" : "text-foreground-500")
                }
              >
                热点现场围观
              </span>
            </span>
          </a>

          <nav className="hidden items-center gap-1 lg:flex">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className={
                  "whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-medium transition-colors " +
                  (light
                    ? "text-background-100 hover:bg-background-50/10 hover:text-background-50"
                    : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950")
                }
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <div className="relative hidden md:block">
              <button
                type="button"
                aria-label="搜索热点"
                onClick={() => setSearchOpen((v) => !v)}
                className={
                  "flex h-10 w-10 items-center justify-center rounded-md transition-colors " +
                  (light ? "text-background-100 hover:bg-background-50/10" : "text-foreground-700 hover:bg-background-100")
                }
              >
                <i className="ri-search-line text-lg" />
              </button>
              {searchOpen && (
                <SearchPanel
                  onPick={() => {
                    setSearchOpen(false);
                  }}
                  onClose={() => setSearchOpen(false)}
                />
              )}
            </div>

            <a
              href="#contact"
              className="hidden whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 lg:inline-flex"
            >
              订阅速递
            </a>

            <button
              type="button"
              aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
              onClick={() => setMobileOpen((v) => !v)}
              className={
                "flex h-10 w-10 items-center justify-center rounded-md lg:hidden " +
                (light ? "text-background-50 hover:bg-background-50/10" : "text-foreground-950 hover:bg-background-100")
              }
            >
              <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-3-line text-2xl"} />
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div className="border-b border-background-200 bg-background-50/95 backdrop-blur-md lg:hidden">
          <nav className="mx-auto grid max-w-[1280px] grid-cols-2 gap-1 px-4 py-4 md:px-6">
            {navLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-2 rounded-lg px-3 py-3 text-sm font-medium text-foreground-800 hover:bg-background-100"
              >
                <i className="ri-arrow-right-s-line text-primary-500" />
                {l.label}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setMobileOpen(false)}
              className="col-span-2 mt-2 inline-flex items-center justify-center whitespace-nowrap rounded-full bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 hover:bg-primary-600"
            >
              订阅热点速递
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}

function SearchPanel({ onPick, onClose }: { onPick: () => void; onClose: () => void }) {
  const [query, setQuery] = useState("");
  const pool = useMemo(() => buildSearchPool(), []);
  const kw = query.trim().toLowerCase();
  const results = kw ? pool.filter((p) => p.title.toLowerCase().includes(kw)) : pool.slice(0, 5);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (results.length > 0) {
      onPick();
      requestAnimationFrame(() => {
        document.querySelector(results[0].target)?.scrollIntoView({ behavior: "smooth" });
      });
    }
  };

  return (
    <div className="absolute right-0 top-12 w-[360px] overflow-hidden rounded-2xl border border-background-200 bg-background-50 shadow-xl">
      <form onSubmit={onSubmit} className="flex items-center gap-2 border-b border-background-200 px-3">
        <i className="ri-search-line text-foreground-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜一搜：票务、新游、热梗…"
          className="h-11 w-full bg-transparent text-sm text-foreground-950 outline-none placeholder:text-foreground-400"
        />
        <button
          type="button"
          aria-label="关闭搜索"
          onClick={onClose}
          className="flex h-7 w-7 items-center justify-center rounded text-foreground-500 hover:bg-background-100"
        >
          <i className="ri-close-line" />
        </button>
      </form>
      <ul className="max-h-[280px] overflow-y-auto p-2">
        {results.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-foreground-500">没有找到相关热点，换个词试试～</li>
        )}
        {results.map((r, idx) => (
          <li key={r.title + idx}>
            <a
              href={r.target}
              onClick={onPick}
              className="group flex items-center justify-between gap-3 rounded-lg px-3 py-2.5 hover:bg-background-100"
            >
              <span className="text-sm text-foreground-800">{r.title}</span>
              <span className="shrink-0 whitespace-nowrap rounded-full bg-secondary-100 px-2 py-0.5 text-[10px] font-medium text-secondary-800">
                {r.tag}
              </span>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}